/**
 * The focus-keyword checklist and score (plan §3), computed in the hub only: instant per keystroke, cheap for the
 * whole route list, and structurally unable to block Publish. The numbers here are the ones Lumio is told (the
 * server's prompt test pins the same table): change both or neither. Pure.
 */
import { footerFirstParagraphText, footerHeadingTexts, footerPlainText, footerWordCount } from './footer-html';
import {
  footerRulesFor,
  resolveFooterText,
  resolveFooterTextIn,
  resolveRouteKeywords,
  resolveRouteText,
  routeOf,
  type KeywordsSource,
} from './seo-document';
import type { Localized, SiteSeoDocument, SiteSeoFooterRule, SiteSeoLiveMeta, SiteSeoRoute } from './seo-types';
import { DESCRIPTION_MAX, DESCRIPTION_MIN, TITLE_MAX } from './seo-validation';

/** The scored checks, in checklist order. */
export const KEYWORD_CHECK_CODES = [
  'title-has-focus',
  'title-starts-with-focus',
  'description-has-focus',
  'footer-first-paragraph',
  'footer-heading',
  'footer-density',
  'title-length',
  'description-length',
  'title-unique',
  'description-unique',
] as const;

export type KeywordCheckCode = (typeof KEYWORD_CHECK_CODES)[number];

const FOOTER_CHECKS: ReadonlySet<KeywordCheckCode> = new Set([
  'footer-first-paragraph',
  'footer-heading',
  'footer-density',
]);

export interface KeywordCheck {
  code: KeywordCheckCode;
  ok: boolean;
  detail?: { density?: number; count?: number; length?: number; duplicateOf?: string };
}

export interface KeywordReport {
  focus: string;
  secondaries: string[];
  source: KeywordsSource;
  checks: KeywordCheck[];
  passed: number;
  total: number;
  /** Info only (addresses are fixed): the path carries the focus. */
  pathHasFocus: boolean;
  /** Info only: how many secondary keywords appear in the title, description or footer. */
  secondariesUsed: { used: number; total: number };
}

export interface KeywordContext {
  path: string;
  language: string;
  defaultLanguage: string;
  liveMeta: Localized<SiteSeoLiveMeta> | null;
  liveFooterRules: readonly SiteSeoFooterRule[];
  footerOverridable: boolean;
  /** The footer rules already built from `liveFooterRules` (`footerRulesFor`), to build them once for many routes. */
  footerRules?: readonly SiteSeoFooterRule[];
}

/** The footer density band, in percent: `occurrences × words(focus) / words(footer) × 100`. */
export const DENSITY_MIN = 0.5;
export const DENSITY_MAX = 2.5;

/** Score tone thresholds (passed / total). */
const GOOD_RATIO = 0.8;
const FAIR_RATIO = 0.5;

/** Lowercase words of letters and digits (accents folded), space-padded so a match is always on whole words. */
export const normaliseForMatch = (s: string): string =>
  ` ${s
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()} `;

const needleOf = (kw: string): string | null => {
  const words = normaliseForMatch(kw).trim();
  return words === '' ? null : ` ${words} `;
};

export const hasKeyword = (text: string, kw: string): boolean => {
  const needle = needleOf(kw);
  return needle !== null && normaliseForMatch(text).includes(needle);
};

/** Whole-word occurrences; two adjacent ones share the space between them, so both count. */
export const countKeyword = (text: string, kw: string): number => {
  const needle = needleOf(kw);
  if (needle === null) return 0;
  const hay = normaliseForMatch(text);
  let count = 0;
  for (let at = hay.indexOf(needle); at !== -1; at = hay.indexOf(needle, at + needle.length - 1)) count += 1;
  return count;
};

const wordsOf = (kw: string): number => normaliseForMatch(kw).trim().split(' ').filter(Boolean).length;

/** The focus starts within the first half of the title. */
const startsWithin = (title: string, kw: string): boolean => {
  const needle = needleOf(kw);
  if (needle === null) return false;
  const hay = normaliseForMatch(title);
  const at = hay.indexOf(needle);
  return at >= 0 && at <= (hay.length - 2) / 2;
};

const sameText = (a: string, b: string): boolean => a.trim().toLowerCase() === b.trim().toLowerCase();

/** The first other exact/pattern route whose OWN value in `language` equals this route's own (never inherited). */
const duplicateOf = (
  doc: SiteSeoDocument,
  route: SiteSeoRoute,
  field: 'title' | 'description',
  language: string,
): string | undefined => {
  const own = route[field]?.[language] ?? '';
  if (own.trim() === '') return undefined;
  return doc.routes.find(
    (r) => r.path !== route.path && r.match !== 'wildcard' && sameText(r[field]?.[language] ?? '', own),
  )?.path;
};

/**
 * The checklist for one exact or pattern route in `ctx.language`, on the texts the site shows (own, else inherited).
 * Null on a wildcard, an unknown path, or without the route's own focus keyword (the site's keywords never score a
 * page). The three footer checks are omitted when the footer is not overridable.
 */
export const keywordReport = (doc: SiteSeoDocument, ctx: KeywordContext): KeywordReport | null => {
  const route = routeOf(doc, ctx.path);
  if (!route || route.match === 'wildcard') return null;
  const { keywords, source } = resolveRouteKeywords(doc, ctx.path, ctx.language, ctx.defaultLanguage);
  const [focus, ...secondaries] = keywords;
  if (focus === undefined) return null;

  const { language, defaultLanguage } = ctx;
  const title = resolveRouteText(doc, ctx.liveMeta, ctx.path, 'title', language, defaultLanguage).value;
  const description = resolveRouteText(doc, ctx.liveMeta, ctx.path, 'description', language, defaultLanguage).value;
  const footer = (
    ctx.footerRules
      ? resolveFooterTextIn(ctx.footerRules, route, language, defaultLanguage)
      : resolveFooterText(doc, ctx.liveFooterRules, route, language, defaultLanguage)
  ).value;

  const footerWords = footerWordCount(footer);
  const count = countKeyword(footerPlainText(footer), focus);
  const density = footerWords === 0 ? 0 : Math.round(((count * wordsOf(focus)) / footerWords) * 100 * 10) / 10;
  const titleDuplicate = duplicateOf(doc, route, 'title', language);
  const descriptionDuplicate = duplicateOf(doc, route, 'description', language);

  const all: Record<KeywordCheckCode, KeywordCheck> = {
    'title-has-focus': { code: 'title-has-focus', ok: hasKeyword(title, focus) },
    'title-starts-with-focus': { code: 'title-starts-with-focus', ok: startsWithin(title, focus) },
    'description-has-focus': { code: 'description-has-focus', ok: hasKeyword(description, focus) },
    'footer-first-paragraph': {
      code: 'footer-first-paragraph',
      ok: hasKeyword(footerFirstParagraphText(footer), focus),
    },
    'footer-heading': { code: 'footer-heading', ok: footerHeadingTexts(footer).some((h) => hasKeyword(h, focus)) },
    'footer-density': {
      code: 'footer-density',
      ok: density >= DENSITY_MIN && density <= DENSITY_MAX,
      detail: { density, count },
    },
    'title-length': {
      code: 'title-length',
      ok: title.length >= 1 && title.length <= TITLE_MAX,
      detail: { length: title.length },
    },
    'description-length': {
      code: 'description-length',
      ok: description.length >= DESCRIPTION_MIN && description.length <= DESCRIPTION_MAX,
      detail: { length: description.length },
    },
    'title-unique':
      titleDuplicate === undefined
        ? { code: 'title-unique', ok: true }
        : { code: 'title-unique', ok: false, detail: { duplicateOf: titleDuplicate } },
    'description-unique':
      descriptionDuplicate === undefined
        ? { code: 'description-unique', ok: true }
        : { code: 'description-unique', ok: false, detail: { duplicateOf: descriptionDuplicate } },
  };
  const checks = KEYWORD_CHECK_CODES.filter((code) => ctx.footerOverridable || !FOOTER_CHECKS.has(code)).map(
    (code) => all[code],
  );

  const texts = [title, description, footerPlainText(footer)];
  return {
    focus,
    secondaries,
    source,
    checks,
    passed: checks.filter((c) => c.ok).length,
    total: checks.length,
    pathHasFocus: hasKeyword(ctx.path, focus),
    secondariesUsed: {
      used: secondaries.filter((kw) => texts.some((text) => hasKeyword(text, kw))).length,
      total: secondaries.length,
    },
  };
};

/**
 * The score of every exact/pattern route with its own focus, for the route-list badges. No live meta (one read per
 * route would be needed): the titles and descriptions are the document's own, default-language or site values.
 */
export const keywordScores = (
  doc: SiteSeoDocument,
  ctx: Omit<KeywordContext, 'path' | 'liveMeta'>,
): Map<string, { passed: number; total: number }> => {
  const scores = new Map<string, { passed: number; total: number }>();
  const footerRules = ctx.footerRules ?? footerRulesFor(doc, ctx.liveFooterRules);
  for (const route of doc.routes) {
    const report = keywordReport(doc, { ...ctx, footerRules, path: route.path, liveMeta: null });
    if (report) scores.set(route.path, { passed: report.passed, total: report.total });
  }
  return scores;
};

export const scoreTone = (passed: number, total: number): 'good' | 'fair' | 'poor' => {
  const ratio = total > 0 ? passed / total : 0;
  return ratio >= GOOD_RATIO ? 'good' : ratio >= FAIR_RATIO ? 'fair' : 'poor';
};
